/*
 * Copyright 2020 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { JsonObject } from '@backstage/config';
import { StructuredMetadataTable } from '../StructuredMetadataTable';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  makeStyles,
  Step as StepUI,
  StepContent,
  StepLabel,
  Stepper,
  Link,
  
} from '@material-ui/core';
import { FormProps, IChangeEvent, UiSchema, withTheme, FieldTemplateProps } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import React, { useState } from 'react';
import { transformSchemaToProps } from './schema';
import {TMNATheme} from 'tmna-theme';

const Form = withTheme(MuiTheme);
type Step = {
  schema: JsonObject;
  title: string;
} & Partial<Omit<FormProps<any>, 'schema'>>;

const useStyles = makeStyles((theme: TMNATheme) => ({
  fieldsContainer: {
    margin: theme.spacing(-2.5, 0),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  metadataTable: {
    maxWidth: '820px',

    '& tr:nth-of-type(odd)': {
      background: theme.palette.tmnaColors.secondary.silverGray,
    }
  },
  preRequisites:{
    display: 'grid',
    '& label':{
      margin: "10px",
    }
  }
}));

type Props = {
  /**
   * Steps for the form, each contains title and form schema
   */
  steps: Step[];
  formData: Record<string, any>;
  onChange: (e: IChangeEvent) => void;
  onReset: () => void;
  onFinish: () => void;
  widgets?: FormProps<any>['widgets'];
  fields?: FormProps<any>['fields'];
};

// TODO: remove this when backstage fix form fields names in structured-metadata table
const prepareStructuredMetadata = (steps: Step[], formData: Record<string, any>) => {
  const fields = steps.reduce(
      (result, step) => {
        return {
          ...result,
          ...((step.schema?.properties as Record<string, any>) || {}),
        }
      }, {} as Record<string, any>,
  );

  return Object.keys(formData).reduce(
      (result, formDataKey) => {
        const fieldName = fields[formDataKey].title || formDataKey;
        return {
          ...result,
          [fieldName]: formData[formDataKey]
        };
      }, {}
  );
}

export function getUiSchemasFromSteps(steps: Step[]): UiSchema[] {
  const uiSchemas: Array<UiSchema> = [];
  steps.forEach(step => {
    const schemaProps = step.schema.properties as JsonObject;
    for (const key in schemaProps) {
      if (schemaProps.hasOwnProperty(key)) {
        const uiSchema = schemaProps[key] as UiSchema;
        uiSchema.name = key;
        uiSchemas.push(uiSchema);
      }
    }
  });
  return uiSchemas;
}

export function getReviewData(formData: Record<string, any>, steps: Step[]) {
  const uiSchemas = getUiSchemasFromSteps(steps);
  const reviewData: Record<string, any> = {};
  for (const key in formData) {
    if (formData.hasOwnProperty(key)) {
      const uiSchema = uiSchemas.find(us => us.name === key);

      if (!uiSchema) {
        reviewData[key] = formData[key];
        continue;
      }

      if (uiSchema['ui:widget'] === 'password') {
        reviewData[key] = '******';
        continue;
      }

      if (!uiSchema['ui:backstage'] || !uiSchema['ui:backstage'].review) {
        reviewData[key] = formData[key];
        continue;
      }

      const review = uiSchema['ui:backstage'].review as JsonObject;
      if (!review.show) {
        continue;
      }

      if (review.mask) {
        reviewData[key] = review.mask;
        continue;
      }
      reviewData[key] = formData[key];
    }
  }

  return reviewData;
}

export const CustomFieldTemplate = ({
  id, help, rawDescription, children, rawErrors,schema
}: FieldTemplateProps) => {   
  const schemaProps = schema as JsonObject;
  const classes = useStyles();
  const isError = rawErrors?.length > 0;
  return (
      <div className={id === 'root' ? classes.fieldsContainer : classes.field}>
        {children}
        {rawDescription && (         
          <FormHelperText variant="outlined" margin="dense" error={isError}>
            {schema.type && schema.type === 'array'? "" : rawDescription}
            {schemaProps.learnmore !== undefined &&
            <>
            <br />
            <Link href={`${schemaProps.learnmore}`} target={"_blank"}>Read More</Link>
            </>}
          </FormHelperText>         
        )}
        
        {isError && rawErrors.map(error => (
            <FormHelperText variant="outlined" margin="dense" error>
              {error}
            </FormHelperText>
        ))}
        {help}
      </div>
  );
}

export const MultistepJsonForm = ({
  steps,
  formData,
  onChange,
  onReset,
  onFinish,
  fields,
  widgets,
}: Props) => {
  
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  
  const handleReset = () => {
    setActiveStep(0);
    onReset();
  };
  const handleNext = () => {
    setActiveStep(Math.min(activeStep + 1, steps.length));
  }
  const handleBack = () => setActiveStep(Math.max(activeStep - 1, 0));
  
  return (
    <>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map(({ title, schema, ...formProps }) => {
          const prerequisitesData = schema.prerequisites || [] as any;
          return (
            <StepUI key={title}>
              <StepLabel>
                {title}
              </StepLabel>
              <StepContent key={title}>
             
              <div className={classes.preRequisites}>
          
              {prerequisitesData?.map((val: any, key: any) =>
                    <label>{key+1+". " + val}</label>
                   )}
                   {

                  schema.learnmore &&
                   <Link href={`${schema.learnmore}`} target={"_blank"}>Learn More</Link>}
        
                </div>

                <Form
                  showErrorList={false}
                  fields={fields}
                  widgets={widgets}
                  noHtml5Validate
                  formData={formData}
                  onChange={onChange}
                  FieldTemplate={CustomFieldTemplate}
                  onSubmit={e => {
                    if (e.errors.length === 0) handleNext();
                  }}
                  {...formProps}
                  {...transformSchemaToProps(schema)}
                >
                  <Box mt={4}>
                    <Grid container spacing={2}>
                      {activeStep !== 0 && (
                        <Grid item>
                          <Button variant="outlined" onClick={handleBack}>
                            Back
                          </Button>
                        </Grid>
                      )}
                      <Grid item>
                        <Button variant="contained" color="primary" type="submit">
                          Next step
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              </StepContent>
            </StepUI>
          );
        })}
        <StepUI key="review">
          <StepLabel>
            Review and create
          </StepLabel>
          <StepContent key="review">
            <div className={classes.metadataTable}>
              <StructuredMetadataTable
                  dense
                  metadata={prepareStructuredMetadata(steps, getReviewData(formData, steps))}
              />
            </div>
            <Box mt={4}>
              <Grid container spacing={2}>
                <Grid item>
                  <Button variant="outlined" onClick={handleBack}>
                    Back
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="outlined" onClick={handleReset}>
                    Reset
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" color="primary" onClick={onFinish}>
                    Create
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </StepContent>
        </StepUI>
      </Stepper>
    </>
  );
};
